import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExpand as faMaximize, faCompress as faMinimize, faSquareXmark, faThumbtack, faThumbtackSlash, faSquareCaretLeft, faSquareCaretRight, faUserGroup, faLocationDot, faBook, faCompass, faCube } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { isNewItemId } from '../biz/itemUtil';
import { SquiggleItem, SavingItem } from '../types/types';
import AliasItem from './AliasItem';
import EditItem from './EditItem';
import './ItemBox.css';
import LinkItem from './LinkItem';
import ViewItem from './ViewItem';
import { useOpenItemsContext } from '../context/OpenItemsContext';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';

type BoxStates = 'editing'|'linking'|'aliasing'|'viewing';

const headings = {
  'quest': 'Quest',
  'personororg': 'Person or Group',
  'miscitem': 'Miscellaneous item',
  'place': 'Place',
  'logentry': 'Log entry',
  'system': 'Application info',
};

const icons = {
  'quest': faCompass,
  'personororg': faUserGroup,
  'miscitem': faCube,
  'place': faLocationDot,
  'logentry': faBook,
  'system': faQuestionCircle
}

const headingPrefixes = {
  editing: 'Editing: ',
  linking: 'Links: ',
  aliasing: 'Names: ',
  viewing: ''
};

function ItemBox({
  item,
  isPinned,
  pinItem,
  close,
  create,
  refresh,
  setEditProtection,
  deleteItem,
  toggleStarItem,
  toggleArchiveItem,
  setIgnoreCanonicalName,
  setCaseSensitiveNames,
  link,
  addLink,
  removeLink,
  setParent,
  addAlias,
  removeAlias,
  uploadImage,
  removeImage,
  imageSeed,
  zoomImage,
  addNewChild,
  flash
} : {
  item : SquiggleItem,
  isPinned: boolean,
  pinItem: (itemId: string, pinned: boolean) => void,
  close: (itemId: string) => void,
  create: (item: SavingItem, parentId?: string) => void,
  refresh: () => void,
  setEditProtection: (itemId: string, protect: boolean) => void,
  deleteItem: (itemId: string) => void,
  toggleStarItem: (item: SquiggleItem) => void,
  toggleArchiveItem: (item: SquiggleItem) => void,
  setIgnoreCanonicalName: (item: SquiggleItem, ignore: boolean) => void,
  setCaseSensitiveNames: (item: SquiggleItem, caseSensitive: boolean) => void,
  link: (itemId: string, selectItem: boolean) => void,
  addLink: (itemId: string, otherId: string) => void,
  removeLink: (itemId: string, otherId: string) => void,
  setParent: (itemId: string, parentId: string) => void,
  addAlias: (itemId: string, alias: string) => void,
  removeAlias: (itemId: string, index: number) => void,
  uploadImage: (file: File, itemId: string) => Promise<JSX.Element|null>,
  removeImage: (itemId: string, imageIndex: number) => Promise<void>,
  imageSeed: number,
  zoomImage: (item: SquiggleItem, imageIndex: number) => void,
  addNewChild: (parentId: string, type: "quest"|"personororg"|"place"|"miscitem"|"logentry"|"system") => void,
  flash: { type: 'none'|'subtle'|'strong' }
}) {
  const [state, setState] = useState<BoxStates>("viewing");
  const [expanded, setExpanded] = useState<boolean>(false);
  const [lastFlash, setLastFlash] = useState<{ type: 'none'|'subtle'|'strong' }>({ type: 'none' });
  const [currentFlashType, setCurrentFlashType] = useState<'none'|'subtle'|'strong'>('none');
  const [focus, setFocus] = useState<boolean>(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const { selectedItemId, setSelectedItemId } = useOpenItemsContext();
  
  useEffect(() => {
    if (isNewItemId(item.id)) {
      setState("editing");
      setEditProtection(item.id, true);
    } else {
      setState("viewing");
    }
    
    setExpanded(false);
  }, [item.id, setEditProtection]);

  useEffect(() => {
    const shouldFlash = (lastFlash !== flash);
    if (shouldFlash) {
      const newFlash = flash;
      let initiallyNonIntersecting = false;
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => { setLastFlash(newFlash); }, (flash?.type === 'strong' || initiallyNonIntersecting) ? 600 : 300);
          observer.disconnect();
        } else {
          initiallyNonIntersecting = true;
          setCurrentFlashType('strong');
        }
      }, { root: document.getElementById('itemarea') });
      observer.observe(boxRef.current!);
      const giveUpTimeout = setTimeout(() => {
        setLastFlash(newFlash);
        observer.disconnect();
      }, 3000);
      return () => { observer.disconnect(); clearTimeout(giveUpTimeout); }
    }
  }, [lastFlash, flash, setLastFlash, boxRef, currentFlashType]);

  useEffect(() => {
    setCurrentFlashType(flash?.type ?? 'none');
  }, [flash]);

  const closeCallback = useCallback(() => {
    if (focus) {
      setFocus(false);
      return;
    }
    close(item.id);
  }, [focus, close, item.id]);

  const isSelected = selectedItemId === item.id;

  useEffect(() => {
    if (isSelected) {
      boxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isSelected, boxRef]);

  useEffect(() => {
    if (!isSelected) {
      return;
    }

    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        setFocus(prevFocus => !prevFocus);
        return;
      }
      if (state === "viewing" && (e.key === 'P' || e.key === "p") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        pinItem(item.id, !isPinned);
        return;
      }
      if (state !== "editing" && (e.key === 'Q' || e.key === "q") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        close(item.id);
        return;
      }
      if (state !== "editing" &&!isPinned && (e.key === 'W' || e.key === "w") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        setExpanded(prevExpanded => !prevExpanded);
        return;
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [isSelected, setFocus, item.id, pinItem, isPinned, close, state, setExpanded]);

  return <div
    className={"outerfocus " + (focus ? "focusitem" : "nofocusitem") + (expanded && !isPinned ? " expanded" : "") + (isPinned && !focus ? " pinned" : "")}
    onMouseDown={(e) => {
      if (focus && e.target === e.currentTarget) {
        setFocus(false);
        e.stopPropagation();
      }
    }}
    onClick={(e) => {
      if (focus && e.target === e.currentTarget) {
        e.stopPropagation();
        return;
      }
      setSelectedItemId(item.id);
      e.stopPropagation();
    }}
  >
    <div className="innerfocus" ref={boxRef}>
      <div className={"itembox " + item.type + " " + (item.archived ? "archived" : "") + " " + (lastFlash !== flash ? `flash${currentFlashType}` : "") + " " + (state === "editing" ? "editing" : "") + (selectedItemId === item.id ? " selected" : "")}>
        <div className="innercontainer">
          <div className="formrow headerbuttonrow">
            <span className="type">
              <FontAwesomeIcon icon={icons[item.type] as IconProp} /> {headingPrefixes[state] + headings[item.type]}
            </span>
            {
              window.innerWidth > 580 &&
              <div
                className="action focus"
                title={focus ? "Unfocus [Ctrl+Shift+Enter]" : "Focus [Ctrl+Shift+Enter]"}
                onClick={() => setFocus(!focus)}>
                <FontAwesomeIcon icon={(focus ? faMinimize : faMaximize) as IconProp} />
              </div>
            }
            {
              !focus && !isPinned && window.innerWidth > 580 &&
              <div
                className="action resize"
                title={expanded ? "Narrow [w]" : "Wide [w]"}
                onClick={(e) => { setExpanded(!expanded); e.stopPropagation(); }}>
                <FontAwesomeIcon icon={(expanded ? faSquareCaretRight : faSquareCaretLeft) as IconProp} />
                <FontAwesomeIcon icon={(expanded ? faSquareCaretLeft : faSquareCaretRight) as IconProp} />
              </div>
            }
            {
              !focus && state === "viewing" && window.innerWidth > 580 &&
              <div
                className="action pin"
                title={isPinned ? "Pin [p]" : "Unpin [p]"}
                onClick={(e) => { pinItem(item.id, !isPinned); e.stopPropagation(); }}>
                <FontAwesomeIcon icon={(isPinned ? faThumbtackSlash : faThumbtack) as IconProp} />
              </div>
            }
            {
              !focus && (state === "viewing" || state === "linking" || state === "aliasing") && <div
                className="action close"
                title="Close [q]"
                onClick={(e) => { closeCallback(); e.stopPropagation(); }}>
                <FontAwesomeIcon icon={faSquareXmark as IconProp} />
              </div>
            }
          </div>
          {
            {
              "editing": () =>
                <EditItem
                  item={item}
                  isSelected={selectedItemId === item.id}
                  create={create}
                  link={link}
                  stopEditing={
                    (newName: string, newText: string) => {
                      item.name = newName;
                      item.text = newText;
                      setState("viewing");
                      setEditProtection(item.id, false);
                      if (isNewItemId(item.id)) {
                        close(item.id);
                      };
                      refresh();
                    }
                  } />,
              "viewing": () =>
                <ViewItem
                  item={item}
                  isSelected={selectedItemId === item.id}
                  edit={() => { setState("editing"); setEditProtection(item.id, true); }}
                  editLinks={() => setState("linking")}
                  editAliases={() => setState("aliasing")}
                  deleteItem={deleteItem}
                  toggleArchiveItem={toggleArchiveItem}
                  toggleStarItem={toggleStarItem}
                  link={link}
                  uploadImage={uploadImage}
                  removeImage={removeImage}
                  imageSeed={imageSeed}
                  zoomImage={zoomImage}
                  addNewChild={addNewChild} />,
              "linking": () =>
                <LinkItem
                  item={item}
                  isSelected={selectedItemId === item.id}
                  back={() => setState("viewing")}
                  link={link}
                  addLink={addLink}
                  removeLink={removeLink}
                  setParent={setParent} />,
              "aliasing": () =>
                <AliasItem
                  item={item}
                  isSelected={selectedItemId === item.id}
                  back={() => setState("viewing")}
                  addAlias={addAlias}
                  removeAlias={removeAlias}
                  setIgnoreCanonicalName={setIgnoreCanonicalName}
                  setCaseSensitiveNames={setCaseSensitiveNames} />
            }[state]()
          }
        </div>
      </div>
    </div>
  </div>;
}

export default ItemBox;